import { useState } from 'react'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { ExcludedProposalItem } from '@/lib/api'

const GROUP_ORDER: ExcludedProposalItem['triageClass'][] = ['form', 'attachment', 'acknowledge']

const GROUP_LABELS: Record<ExcludedProposalItem['triageClass'], [string, string]> = {
  form: ['form', 'forms'],
  attachment: ['attachment', 'attachments'],
  acknowledge: ['acknowledgement', 'acknowledgements'],
}

function countLabel(count: number, cls: ExcludedProposalItem['triageClass']): string {
  return `${count} ${GROUP_LABELS[cls][count === 1 ? 0 : 1]}`
}

interface ExcludedItemsStripProps {
  items: ExcludedProposalItem[]
}

/**
 * Accounts for every non-response checklist item — silent omission reads as
 * "the AI missed it". Collapsed by default: this is deliberately secondary
 * content; the summary line is the accounting, the detail is on demand.
 */
export function ExcludedItemsStrip({ items }: ExcludedItemsStripProps) {
  const [expanded, setExpanded] = useState(false)

  if (items.length === 0) return null

  const groups = GROUP_ORDER.map((cls) => ({
    cls,
    items: items.filter((i) => i.triageClass === cls),
  })).filter((g) => g.items.length > 0)

  const summary = groups.map((g) => countLabel(g.items.length, g.cls)).join(' · ')

  return (
    <div className="rounded-xl border border-dashed border-border">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-2 px-4 py-3 text-left"
        aria-expanded={expanded}
      >
        <ChevronRight
          className={cn(
            'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
            expanded && 'rotate-90'
          )}
        />
        <span className="text-sm">
          Not included: <span className="text-muted-foreground">{summary}</span>
        </span>
        <span className="ml-auto shrink-0 text-meta">
          no documents created
        </span>
      </button>

      {expanded && (
        <div className="space-y-4 px-4 pb-4 pl-10">
          {groups.map((group) => (
            <div key={group.cls}>
              <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                {GROUP_LABELS[group.cls][1]}
              </p>
              <ul className="space-y-2">
                {group.items.map((item) => (
                  <li key={item.submissionRequirementId} className="text-sm">
                    <p className="text-foreground">{item.title}</p>
                    <p className="mt-0.5 text-meta">{item.reason}</p>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
